import { css } from '@emotion/css';
import { ActionIcon, Button, Grid, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { IconPlayerPlay, IconSparkles, IconX } from '@tabler/icons-react';
import { animatedGradientText } from 'app/Private/Clients/Detail/InfoTab/components/AiSummaryPanel/AiSummaryPanel.styles';
import { ClientHoverCard } from 'components/ClientHoverCard';
import { Panel } from 'components/Panel';
import dayjs from 'dayjs';
import { tKeys, useTranslation } from 'locales';
import type { AiCall, ReminderAiCall } from 'modules/AiCalls/api';
import { AiCallLoader } from 'modules/AiCalls/components';
import { useState } from 'react';
import { isNonNullish, isNullish } from 'remeda';
import { splitTranscript } from '../utils';
import { ConversationMessage } from './ConversationMessage';
import { ReminderDetailItem } from './ReminderDetailItem';

type AiCallTabProps = {
  aiCall: AiCall | ReminderAiCall;
  debtorId: AiCall['debtorId'];
  debtorFullName: AiCall['debtorName'];
  isLoading?: boolean;
};

export const AiCallTab = ({
  aiCall,
  debtorId,
  debtorFullName,
  isLoading = false,
}: AiCallTabProps) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const [shouldShowAudioPlayer, setShouldShowAudioPlayer] = useState<boolean>(false);

  if (isNullish(aiCall)) {
    return (
      <Text c="dimmed" fz="sm">
        {t(tKeys.REMINDER.AI_CALL_NO_CONTENT_AVAILABLE)}
      </Text>
    );
  }

  const { feedback } = aiCall;

  if (isLoading) return <AiCallLoader />;

  return (
    <Grid>
      <Grid.Col span={6}>
        <Panel title="Details" withBorder h="100%">
          <Stack flex={1}>
            <ReminderDetailItem
              label={t(tKeys.REMINDER.CLIENT)}
              value={<ClientHoverCard debtorId={debtorId} debtorName={debtorFullName} />}
            />
            <ReminderDetailItem
              label={t(tKeys.REMINDER.AI_CALL_PHONE)}
              value={<Text>{aiCall.to}</Text>}
            />
            {isNonNullish(feedback) && (
              <>
                <ReminderDetailItem
                  label={t(tKeys.REMINDER.AI_CALL_DATE)}
                  value={<Text>{dayjs(feedback.startedAt).format('LLLL')}</Text>}
                />
                <ReminderDetailItem
                  label={t(tKeys.REMINDER.AI_CALL_END_REASON)}
                  value={<Text>{feedback.endReason}</Text>}
                />
                <ReminderDetailItem
                  label={t(tKeys.REMINDER.AI_CALL_OUTCOME)}
                  value={
                    <Text>
                      {feedback.outcome ?? t(tKeys.REMINDER.AI_CALL_NO_OUTCOME_AVAILABLE)}
                    </Text>
                  }
                />
              </>
            )}
            {'retryCount' in aiCall && aiCall.retryCount > 0 && (
              <ReminderDetailItem
                label={t(tKeys.REMINDER.RETRY_COUNT)}
                value={<Text>{aiCall.retryCount}</Text>}
              />
            )}
          </Stack>
        </Panel>
      </Grid.Col>
      <Grid.Col span={6}>
        {isNonNullish(feedback) && (
          <Panel
            h="100%"
            className={gradiantBackground}
            title={
              <Group>
                <IconSparkles stroke={1.5} size={20} color={theme.colors.purple[6]} />
                <Text className={animatedGradientText} fw="bold">
                  {t(tKeys.REMINDER.AI_CALL_SUMMARY)}
                </Text>
              </Group>
            }
          >
            <Text c="dark">
              {feedback.summary ?? t(tKeys.REMINDER.AI_CALL_NO_SUMMARY_AVAILABLE)}
            </Text>
          </Panel>
        )}
      </Grid.Col>
      {isNonNullish(feedback) && (
        <Grid.Col span={12}>
          <Panel
            title={t(tKeys.REMINDER.AI_CALL_TRANSCRIPT)}
            topRight={
              feedback.recordingUrl &&
              (!shouldShowAudioPlayer ? (
                <Button
                  variant="light"
                  leftSection={<IconPlayerPlay size={16} />}
                  onClick={() => setShouldShowAudioPlayer(true)}
                >
                  {t(tKeys.REMINDER.PLAY_RECORDING)}
                </Button>
              ) : (
                <Group justify="space-between" align="center">
                  <audio controls>
                    <source src={feedback.recordingUrl} type="audio/wav" />
                  </audio>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => setShouldShowAudioPlayer(false)}
                    size="sm"
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Group>
              ))
            }
            withBorder
          >
            <Stack gap="md" mt="lg">
              {feedback.transcript
                ? splitTranscript(feedback.transcript).map(message => (
                    <ConversationMessage key={message.index} message={message} />
                  ))
                : t(tKeys.REMINDER.AI_CALL_NO_TRANSCRIPT_AVAILABLE)}
            </Stack>
          </Panel>
        </Grid.Col>
      )}
    </Grid>
  );
};

const gradiantBackground = css`
  background: radial-gradient(
    circle at 75% 5%,
    rgba(139, 92, 246, 0.25) 15%,
    rgba(249, 140, 146, 0.15) 65%,
    transparent 100%
  );
`;
